import { useMemo } from 'react';

import { useSessionStore } from './sessionStore';

/**
 * PA-7 — the frontend edge of the standardized permission taxonomy [STD-API-6 defense-in-depth].
 * All gating derives from the caller's `/users/me` permission set (mirrored in the session store);
 * the backend still enforces 403. Codes are the canonical dotted `{service}.{module}.{action}`.
 */

const EMPTY: readonly string[] = [];

/** Exact-code check. */
export function permissionsInclude(permissions: readonly string[], code: string): boolean {
  return permissions.includes(code);
}

/**
 * MENU-PERM — true when the user holds the module's explicit `.menu` permission.
 *
 * ⚠ THIS REVERSES PA-7. It used to return "does the user hold ANY action on this module", making
 * navigation a SIDE EFFECT of any other grant. There was then no way to say "read this module's data
 * but keep it out of my menu" — and the case that forced the change is real: migration 318 seeds
 * `definitions.entries.view` to every role because every profile form needs to resolve country and
 * gender values, which under the old rule lit the Definitions module up for all of them.
 *
 * Menu visibility is now granted, not inferred: exactly `{service}.{module}.menu`.
 *
 * ⚠ `.menu` GRANTS NO DATA ACCESS. It is never enforced on an endpoint, so a role holding
 * `identity.users.menu` without `identity.users.view` gets a nav entry whose page 403s. The Roles
 * matrix warns when that combination is ticked; nothing here can detect it.
 */
export function permissionsHaveModule(permissions: readonly string[], moduleCode: string): boolean {
  return permissions.includes(`${moduleCode}.menu`);
}

export interface PermissionChecker {
  readonly permissions: readonly string[];
  /** Exact code, e.g. `learning.courses.add`. */
  can(code: string): boolean;
  /** MENU-PERM: the module's explicit `{service}.{module}.menu` grant (no longer derived). */
  canModule(moduleCode: string): boolean;
  /** Any-of: at least one of the codes is held. */
  canAny(codes: readonly string[]): boolean;
}

export function createPermissionChecker(permissions: readonly string[]): PermissionChecker {
  return {
    permissions,
    can: (code) => permissionsInclude(permissions, code),
    canModule: (moduleCode) => permissionsHaveModule(permissions, moduleCode),
    canAny: (codes) => codes.some((c) => permissionsInclude(permissions, c))
  };
}

/** Memoized permission checker for the current session (Zustand — no `authApi` threading). */
export function usePermissions(): PermissionChecker {
  const permissions = useSessionStore((s) => s.user?.permissions ?? EMPTY);
  return useMemo(() => createPermissionChecker(permissions), [permissions]);
}

/** Boolean for a single exact permission code. */
export function usePermission(code: string): boolean {
  return usePermissions().can(code);
}
